import {AvailableDepartureTripModel, AvailableReturnTripModel, AvailableTripModel} from "./available-trip.model";
import {AvailableFareWithDiscountModel} from "./fares/available-fare-with-discount.model";
import {computed, makeObservable} from "mobx";
import {NullableDate, NullableString} from "../../../types/nullable-types";
import {ILowFareResult} from "../../low-fare/low-fare-readers/low-fare-reader.interface";
import {IFareToSell} from "../../booking/models/booking-view-model.interface";
import {IFareDiscountType} from "./discount-types/fare-discount-type.interface";
import {BogoFareDiscount} from "./discount-types/bogo-fare-discount";
import {NullableStation} from "../../stations/station.service.interface";
import {IAvailabilityViewModel} from "./availability-view-model.interface";
import {IFlightsScheduleViewModel} from "../flights-schedule/flights-schedule-view-model.interface";
import {
    IFlightSearchControllerViewModel
} from "../flight-search-controller/flight-search-controller-view-model.interface";
import {Price} from "../../currency/price";
import {IAvailabilityFareReducer} from "./fare-reducer.interface";
import {FlightScheduleAvailabilityStatusEnum} from "../../flights-schedule/flights-schedule.service.interface";
import {IBookingStrategy} from "../../booking/booking-strategies/booking-strategy.interface";
import {
    IDotRezAvailableTrip
} from "../../dot-rez-api/data-contracts/booking/search-simple/search-simple.data-contracts";


export class AvailabilityModel implements IAvailabilityViewModel{
    constructor(public readonly searchController: IFlightSearchControllerViewModel,
                private readonly farePriceReducer: IAvailabilityFareReducer,
                private readonly bookingStrategy: IBookingStrategy) {

        makeObservable(this, {
            departureTrips: computed,
            returnTrips: computed
        });
    }

    get pricingInformationMessage(): string {
        return this.farePriceReducer.availabilityPricingInformationMessage;
    }

    get departureTrips(): AvailableDepartureTripModel[] {
        const defaultSearchResult = this.searchController.departureSearchResult;
        const standBySearchResult = this.searchController.standByDepartureSearchResult;

        return defaultSearchResult.trips.map(trip => {
            let standByTrip: IDotRezAvailableTrip | null = null;
            if(standBySearchResult) {
                standByTrip = standBySearchResult.trips.find(t => t.date === trip.date) ?? null;
            }
            return new AvailableDepartureTripModel(this,
                trip,
                standByTrip,
                this.bookingStrategy,
                defaultSearchResult.faresAvailable,
                standBySearchResult?.faresAvailable ?? {});
        })
        .filter(trip => trip.journeys.length > 0);
    }

    get returnTrips(): AvailableReturnTripModel[] {

        const defaultSearchResult = this.searchController.returnSearchResult;
        const standBySearchResult = this.searchController.standByReturnSearchResult;

        return defaultSearchResult.trips.map(trip => {
            let standByTrip: IDotRezAvailableTrip | null = null;
            if(standBySearchResult) {
                standByTrip = standBySearchResult.trips.find(t => t.date === trip.date) ?? null;
            }
            return new AvailableReturnTripModel(this,
                trip,
                standByTrip,
                this.bookingStrategy,
                defaultSearchResult.faresAvailable,
                standBySearchResult?.faresAvailable ?? {});
        })
        .filter(trip => trip.journeys.length > 0);



    }

    get isAvailabilitySearchWithBlueBenefits(): boolean {
        return this.searchController.withBlueBenefits;
    }

    get showStandardAndBlueBenefitsPrice(): boolean {
        return this.isAvailabilitySearchWithBlueBenefits && this.farePriceReducer.showStandardAndBlueBenefitsPrice;
    }

    get departureOrigin(): NullableStation {
        return this.searchController.departureOrigin;
    }

    get departureDestination(): NullableStation {
        return this.searchController.departureDestination;
    }

    get returnOrigin(): NullableStation {
        return this.searchController.returnOrigin;
    }

    get returnDestination(): NullableStation {
        return this.searchController.returnDestination;
    }

    get departureDate(): NullableDate {
        return this.searchController.departureDate;
    }

    get returnDate(): NullableDate {
       return this.searchController.returnDate;
    }

    get departureFlightSchedule(): IFlightsScheduleViewModel {
        return this.searchController.departureFlightSchedule;
    }

    get returnFlightSchedule(): IFlightsScheduleViewModel {
        return this.searchController.returnFlightSchedule;
    }

    getDepartureLowFare(date: Date): ILowFareResult {
        return this.bookingStrategy.getLowFaresReader().getDepartureLowFare(date);
    }
    getReturnLowFare(date: Date): ILowFareResult {
        return this.bookingStrategy.getLowFaresReader().getReturnLowFare(date);
    }

    get isOneWayTrip(): boolean {
        return this.searchController.isOneWayDepartureTrip;
    }

    private get allTrips(): AvailableTripModel[] {
        return [
            ...this.departureTrips,
            ...this.returnTrips
        ]
    }

    get hasDepartureFareSelected(): boolean {
        return this.departureTrips.some(t => t.hasFareSelected)
    }


    get hasReturnFareSelected(): boolean {
        return this.returnTrips.some(t => t.hasFareSelected);
    }

    private _createFareToSell(fare: AvailableFareWithDiscountModel): IFareToSell {
        // if any of the journeys have BOGO available then we will use BOGO to make the search,
        // otherwise if BOGO is available only on return journey and we sell departure journey without BOGO
        // then we won't be able to sell return journey with BOGO.
        const discount = this._overrideWithBogo(fare.discount);
        return {
            journeyKey: fare.journeyKey,
            isStandBy: fare.isStandBy,
            fareAvailabilityKey: fare.fareKey,
            infantCount: this.getNumberOfInfants(),
            passengerTypes: discount.createPassengerTypesForTripSell(this.searchController.passengers),
            withBlueBenefits: fare.hasBlueBenefits,
            designator: fare.designator
        };
    }

    reduceDepartureFarePrice(price: Price): Price {
        return this.farePriceReducer.reduceDepartureFare(price);
    }

    async sellDepartureJourney(fare: AvailableFareWithDiscountModel): Promise<void> {
        await this.bookingStrategy.booking.sellDepartureJourney(this._createFareToSell(fare));

    }

    reduceReturnFarePrice(price: Price): Price {
        return this.farePriceReducer.reduceReturnFare(price);
    }

    async sellReturnJourney(fare: AvailableFareWithDiscountModel): Promise<void> {
        await this.bookingStrategy.booking.sellReturnJourney(this._createFareToSell(fare));

    }

    get departureBundleNameToShowOnFare(): NullableString {
        return this.farePriceReducer.departureBundleNameToShowOnFare;
    }

    get returnBundleNameToShowOnFare(): NullableString {
        return this.farePriceReducer.returnBundleNameToShowOnFare;
    }

    getNumberOfInfants(): number {
        return this.searchController.passengers.countInfants();
    }

    private _overrideWithBogo(discount: IFareDiscountType): IFareDiscountType {
        if(discount instanceof BogoFareDiscount) {
            return discount;
        }

        for(let trip of this.allTrips) {
            for(let journey of trip.journeys) {
                for(let fare of journey.fares) {

                    if(fare.discount instanceof BogoFareDiscount) {
                        return fare.discount;
                    }
                }
            }
        }

        return discount;
    }

    async changeDepartingDate(newDepartureDate: Date): Promise<void> {
        if (newDepartureDate.getTime() === this.departureDate?.getTime()) {
            return;
        }

        this.searchController.departureDate = newDepartureDate;

        if(this.searchController.returnDate) {
            if(newDepartureDate.getTime() > this.searchController.returnDate.getTime()) {
                this.searchController.returnDate =  this.returnFlightSchedule.datesRange.find(d => d.getTime() > newDepartureDate.getTime()
                                                                                                        && this.returnFlightSchedule.getDateAvailability(d).status === FlightScheduleAvailabilityStatusEnum.Available)
                                                    ?? newDepartureDate;
                await this.searchController.applySearch();
                return;
            }
        }

        await this.searchController.applySearchForDepartureOnly()
        this.bookingStrategy.services.analytics.flightSelectionEvents.logViewItemListForDeparture(this);
    }

    async changeReturnDate(newReturnDate: Date): Promise<void> {
        if (newReturnDate.getTime() === this.searchController.returnDate?.getTime()) {
            return;
        }

        this.searchController.returnDate = newReturnDate;

        await this.searchController.applySearchForReturnOnly();
        this.bookingStrategy.services.analytics.flightSelectionEvents.logViewItemListForReturn(this);
    }



    dispose(): void {
    }
}
